import { $axios, useAuthTokenError, objectToUrlParams } from '../utils'
import '../typedefs'
import { validateSignup } from '../validators/auth.validators'
import { validateUpdateUser, validateDeleteUser, validateUpdateRole } from '../validators/users.validators'

/**
 * @description Get all users
 * @param {string} authToken
 * @returns {import('axios').AxiosResponse<User[]>}
 */
export function getUsers (authToken, queryObject = {}) {
  useAuthTokenError(authToken)
  const queryString = objectToUrlParams(queryObject)
  return $axios(`/users?${queryString}`, {
    method: 'GET',
    headers: {
      'x-access-token': authToken
    }
  })
}

/**
 * Gets a single user by userId
 * @param {String} authToken Auth token
 * @param {String} userId User ID
 * @returns {import('axios').AxiosResponse<User>}
 */
export function getUserById (authToken, userId) {
  useAuthTokenError(authToken)

  return $axios(`/users/${userId}`, {
    method: 'GET',
    headers: {
      'x-access-token': authToken
    }
  })
}

/**
 * Creates a new user
 * @param {String} authToken AuthToken
 * @param {User} user User object
 * @returns {import('axios').AxiosResponse<User>}
 */
export function createUser (authToken, user) {
  validateSignup(user)
  useAuthTokenError(authToken)

  return $axios('/users', {
    method: 'POST',
    headers: {
      'x-access-token': authToken
    },
    data: user
  })
}

/**
 * Updates a user
 * @param {String} authToken Auth token
 * @param {User} payload User object
 */
export function updateUser (authToken, payload) {
  useAuthTokenError(authToken)
  validateUpdateUser(payload)

  return $axios(`/users/${payload.id}`, {
    method: 'PUT',
    headers: {
      'x-access-token': authToken
    },
    data: payload
  })
}

/**
 * Updates a user's role
 * @param {String} authToken Auth token
 * @param {User} payload User object
 */
export function updateRole (authToken, payload) {
  useAuthTokenError(authToken)
  validateUpdateRole(payload)

  return $axios(`/users/${payload.id}/role`, {
    method: 'PUT',
    headers: {
      'x-access-token': authToken
    },
    data: payload
  })
}

/**
 * Updates a user's avatar
 * @param {String} authToken Auth token
 * @param {{ userId: String, formData: FormData}} payload User object
 */
export function updateUserAvatar (authToken, { userId, formData }) {
  return $axios(`/users/${userId}/uploadAvatar`, {
    method: 'PUT',
    headers: {
      'x-access-token': authToken,
      'Content-Type': 'multipart/form-data'
    },
    data: formData
  })
}

/**
 * Deletes a user
 * @param {String} authToken Auth token
 * @param {String} userId User object
 */
export function deleteUser (authToken, userId) {
  useAuthTokenError(authToken)
  validateDeleteUser(userId)

  return $axios(`/users/${userId}`, {
    method: 'DELETE',
    headers: {
      'x-access-token': authToken
    }
  })
}
